import type { ClientDetails, ClientDetailsResponse, ClientGroupDetails, ClientGroupResponse, ClientHistoryCommissionResponse, ClientListResponse, GroupId, Rules, SuperAgentResponse } from "@/types/customerManagement";
import request from "@/utils/request";

export const getRules = async (): Promise<Rules> => {
    const res = await request.post<Rules>('/manage/rule/get');
    return res;
}

export const fetchClientList = async (query: any, page_no: number, page_size: number): Promise<ClientListResponse> => {
    const res = await request.post<ClientListResponse>('/user/list', { ...query, page_no, page_size });
    return {
        list: (res.list || []).map((item: any) => ({
            uid: item.user_id,
            registrationTime: item.created_time,
            customerType: item.type,
            superiorAgentUID: item.superior_id,
            username: item.user_name,
            tradingVolume: item.total_trading_volume,
            handlingFee: item.total_handling_fee,
            commissions: item.total_commissions,
            deposit: item.total_deposit,
            associatedGroup: item.group_name,
            assetRange: item.asset_range,
            futuresRatio: item.reward_ratio,
            spotRatio: item.spot_ratio,
            label: item.remark,
            firstDepositTime: item.first_deposit_time,
            firstTransactionTime: item.first_transaction_time,
            lastTransactionTime: item.latest_transaction_time,
            isAgent: item.is_agent // whether the client is also an agent

        })),
        total: res.total
    };
};

// View Details
export const fetchClientDetails = async (query: any): Promise<ClientDetails> => {
    const res = await request.post<ClientDetailsResponse>('/agent/detail', { ...query });
    return {
        uid: res.user_id,
        registrationTime: res.registration_time,
        customerType: res.type,
        superiorAgentUID: res.superior_id,
        email: res.user_name,
        group: res.group_name,
        futuresRatio: res.reward_ratio,
        spotRatio: res.spot_ratio,
    };
};

export const addClient = async (data: {
    user_id: string;
    reward_ratio?: number;
    spot_ratio?: number;
    rake_back_time: number;
    valid: boolean;
}): Promise<any> => {
    return await request.post('/agent/add', data);
};

export const modifyClient = async (data: {
    user_id: string;
    reward_ratio: number;
    spot_ratio: number;
    rake_back_time: number;
    remark?: string;
    valid: boolean;
    // Add other fields as needed
}): Promise<any> => {
    return await request.post('/agent/modify', data);
};

export const modifyClientRemark = async (data: {
    user_id: string;
    remark?: string;
    // Add other fields as needed
}): Promise<any> => {
    return await request.post('/user/remark', data);
};

export const exportClientList = async (query: any): Promise<ClientListResponse> => {
    const res = await request.post<ClientListResponse>('/user/export', { ...query, page_no: 1, page_size: 10 });
    return {
        list: (res.list || []).map((item: any) => ({
            uid: item.user_id,
            registrationTime: item.created_time,
            customerType: item.type,
            superiorAgentUID: item.superior_id,
            username: item.user_name,
            // associatedGroup: item.group_name,
            assetRange: item.asset_range,
            futuresRatio: item.reward_ratio,
            spotRatio: item.spot_ratio,
            label: item.remark,
            firstTransactionTime: item.first_transaction_time,
            lastTransactionTime: item.latest_transaction_time,
            isAgent: item.is_agent // whether the client is also an agent
        })),
        total: res.total
    };
}

export const fetchHistory = async (query: any, page_no: number, page_size: number): Promise<ClientHistoryCommissionResponse> => {
    const res = await request.post<ClientHistoryCommissionResponse>('/agent/history', { ...query, page_no, page_size });
    return {
        list: (res.list || []).map((item: any) => ({
            uid: item.user_id,
            transactionUID: item.order_id,
            transactionMethod: [item.entrust_type, item.order_type],
            // order_type 0: 市价单 1：计划单 2：止盈单 4：止损单 5：强平单 0: Market Order 1: Limit Order 2: Take Profit Order 4: Stop Loss Order 5: Forced Liquidation Order
            // entrust_type 0-市价 1-限价 2-强平单 0-Market Price 1-Limit Price 2-Liquidation Order
            tradingPair: item.contract_code,
            direction: item.side,
            subjectiveMethod: item.role,
            averageTransactionPrice: item.trade_price,
            executedOrderedVolume: [item.quote_volume, item.usdt_volume],
            transactionFee: item.cost_fee,
            orderStatus: item.order_status,
            // order_status 0-default 100-user revocation 101-market unfilled revocation 102-background revocation 200-full 201-partial retracted 202-partial slot
            creationTime: item.create_time
        })),
        total: res.total
    };
};

export const fetchGroupID = async (query: any): Promise<GroupId[]> => {
    const res = await request.post<GroupId[]>('/agent/group/id/list', { ...query });
    return res.map((item: any) => ({
        groupId: item.id,
        groupName: item.group_name
    }));
};

export const fetchClientGroupList = async (query: any, page_no: number, page_size: number): Promise<ClientGroupResponse> => {
    const res = await request.post<ClientGroupResponse>('/agent/group/list', { ...query, page_no, page_size });
    return (
        {
            list: (res.list || []).map((item: any) => ({
                groupId: item.id,
                groupName: item.group_name,
                creationTime: item.created_at,
                memberCount: item.member_count,
                depositorCount: item.depositor_count, //
                totalDeposit: item.total_deposit, //
                totalVolume: item.total_volume, //
                totalCommission: item.total_commission, //
                updatedAt: item.updated_at,
                description: item.description,
                spotRatio: item.spot_rebate_rate,
                futuresRatio: item.contract_rebate_rate,
                alphaRatio: item.alpha_rebate_rate
            })),
            total: res.total
        }
    );
};

export const fetchClientGroupDetails = async (query: any): Promise<ClientGroupDetails> => {
    const res = await request.post<any>('/agent/group/detail', { ...query });
    const group = res.group || {};
    return {
        group: {
            groupId: group.id,
            groupName: group.group_name,
            creationTime: group.created_at,
            memberCount: group.member_count,
            depositorCount: group.depositor_count,
            totalDeposit: group.total_deposit,
            totalVolume: group.total_volume,
            totalCommission: group.total_commission,
            updatedAt: group.updated_at,
            description: group.description,
            spotRatio: group.spot_rebate_rate,
            futuresRatio: group.contract_rebate_rate,
            alphaRatio: group.alpha_rebate_rate
        },
        members: (res.members || []).map((item: any) => ({
            userId: String(item.user_id),
            joinedAt: Number(item.joined_at),
            userDetail: {
                email: item.user_detail.user_name,
                lastLoginAt: Number(item.user_detail.last_login_at)
            }
        }))
    };
};

export const fetchAgentID = async (query: any, type?: number): Promise<string[]> => {
    const res = await request.post<string[]>('/agent/group/agent/list', { ...query, type });
    return res;
};

export const saveClientGroup = async (data: any): Promise<any> => {
    const res = await request.post<any>('/agent/group/upsert', { ...data });
    return res;
};

// upsert the member
export const upsertClientGroupMember = async (data: any): Promise<any> => {
    const res = await request.post<any>('/agent/group/member', { ...data });
    return res;
};

export const fetchSuperAgentList = async (query: any, page_no: number, page_size: number): Promise<SuperAgentResponse> => {
    const res = await request.post<SuperAgentResponse>('/agent/superagent/list', { ...query, page_no, page_size });
    return (
        {
            list: (res.list || []).map((item: any) => ({
                superAgentUID: item.user_id,
                timeToBecomeSuperAgent: item.agent_time,
                invitationCode: item.invite_code,
                registrationTime: item.created_time,
                totalInvites: item.registration_count || 0,
                agentCount: item.agent_count, // || 0,
                rebateRatio: item.reward_ratio,
                spotRatio: item.spot_ratio,
                groupName: item.group_name,
                username: item.user_name,
                modelId: item.model_id
            })),
            total: res.total
        }
    );
};

export const upsertSuperAgent = async (data: any): Promise<any> => {
    const res = await request.post<any>('/agent/superagent/upsert', { ...data });
    return res;
};
